import React, { useState, useEffect, useRef } from "react";
import {
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
} from "recharts";
import { COLORS, SHADOWS } from "../../../../Theme/variables";
import "./EnvironmentReportStyles.css";
import orderBy from "../../../../Utilities/genericArraySorter";
import ToolTip from "./ToolTip";

const climateScoreColors = {
  1: "#75AC4A",
  2: "#9CA43E",
  3: "#B99E34",
  4: "#D9982C",
  5: "#FB9122",
};

const EmissionsPerCategory = ({ list }) => {
  const [data, setData] = useState([]);
  const [selectedCat, setSelectedCat] = useState("all");
  const [selectedSubCat, setSelectedSubCat] = useState("");
  const [totalAvg, setTotalAvg] = useState(0);
  const [itemsBySubCat, setItemsBySubCat] = useState([]);

  var colorArray = [
    "#FF6633",
    "#FFB399",
    "#FF33FF",
    "#00B3E6",
    "#E6B333",
    "#3366E6",
    "#999966",
    "#99FF99",
    "#B34D4D",
    "#80B300",
    "#809900",
    "#E6B3B3",
    "#6680B3",
    "#66991A",
    "#FF99E6",
    "#CCFF1A",
    "#FF1A66",
    "#E6331A",
    "#33FFCC",
    "#66994D",
    "#B366CC",
    "#4D8000",
    "#B33300",
    "#CC80CC",
    "#66664D",
    "#991AFF",
    "#E666FF",
    "#4DB3FF",
    "#1AB399",
    "#E666B3",
    "#33991A",
    "#CC9999",
    "#B3B31A",
    "#00E680",
    "#4D8066",
    "#809980",
    "#E6FF80",
    "#1AFF33",
    "#999933",
    "#FF3380",
    "#CCCC00",
    "#66E64D",
    "#4D80CC",
    "#9900B3",
    "#E64D66",
    "#4DB380",
    "#FF4D4D",
    "#99E6E6",
    "#6666FF",
  ];

  const handleShowAllClick = () => {
    setSelectedSubCat("");
    setSelectedCat("all");
  };

  const generateChart = () => {
    let tempData = {};
    let tempTopList = [];
    let tempItemsBySubCat = {};
    let totalScore = 0;
    let totalItems = 0;

    list.forEach((item) => {
      if (item.carbon_fp && item.cat_id !== "") {
        if (tempItemsBySubCat[item.subcat_name]) {
          tempItemsBySubCat[item.subcat_name].push({
            name: item.name,
            climateScore: item.carbon_fp.climateScore,
          });
        } else {
          tempItemsBySubCat[item.subcat_name] = [
            { name: item.name, climateScore: item.carbon_fp.climateScore },
          ];
        }
        totalScore += item.carbon_fp.climateScore;
        totalItems += 1;
        tempTopList.push({
          name: item.name,
          climateScore: item.carbon_fp.climateScore,
        });

        if (tempData[item.cat_name]) {
          tempData[item.cat_name].nrOfItems += 1;
          tempData[item.cat_name].climateSum += item.carbon_fp.climateScore;
          if (tempData[item.cat_name][item.subcat_name]) {
            tempData[item.cat_name][item.subcat_name].nrOfItems += 1;
            tempData[item.cat_name][item.subcat_name].climateSum +=
              item.carbon_fp.climateScore;
          } else {
            tempData[item.cat_name][item.subcat_name] = {
              nrOfItems: 1,
              climateSum: item.carbon_fp.climateScore,
            };
          }
        } else {
          tempData[item.cat_name] = {
            nrOfItems: 1,
            climateSum: item.carbon_fp.climateScore,
          };
          tempData[item.cat_name][item.subcat_name] = {
            nrOfItems: 1,
            climateSum: item.carbon_fp.climateScore,
          };
        }
      }
    });
    let formattedData = [];
    if (selectedCat === "all") {
      Object.keys(tempData).forEach((item) => {
        let climateAvg = tempData[item].climateSum / tempData[item].nrOfItems;
        formattedData.push({
          name: item,
          climateAvg: climateAvg.toFixed(2),
          color: colorArray.pop(),
        });
      });
    } else {
      Object.keys(tempData[selectedCat.name]).forEach((item) => {
        if (item !== "nrOfItems" && item !== "climateSum") {
          let climateAvg =
            tempData[selectedCat.name][item].climateSum /
            tempData[selectedCat.name][item].nrOfItems;
          formattedData.push({
            name: item,
            climateAvg: climateAvg.toFixed(2),
            color: colorArray.pop(),
          });
        }
      });
    }
    let tempTotalAvg = totalScore / totalItems;
    setTotalAvg(tempTotalAvg.toFixed(2));
    setData(orderBy(formattedData, "climateAvg"));
    setItemsBySubCat(tempItemsBySubCat);
  };

  useEffect(() => {
    generateChart();
  }, [selectedCat]);
  return (
    <div>
      {data.length > 0 ? (
        <React.Fragment>
          <BarChart
            width={350}
            height={250}
            data={data}
            margin={{ top: 10, right: 5, bottom: 5, left: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis hide={true} height={20} />
            <YAxis
              width={30}
              interval={0}
              type="number"
              domain={[0, 5]}
              ticks={[0, 2.5, 5]}
              tickSize={3}
            />
            <Tooltip content={<ToolTip />} />
            <Bar dataKey="climateAvg" fill={COLORS.primary} maxBarSize={35}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
          {selectedCat !== "all" && (
            <button
              id="emissionBackButton"
              onClick={() => handleShowAllClick()}
            >
              <img
                src={require("../../../../Images/Icons/back.svg")}
                className="backBtnIcon"
                alt="backIcon"
              />
            </button>
          )}
          <div id="emissionsTitleRow">
            <h5 id="emissionsCategoryTitle">
              {selectedCat === "all" ? "All categories" : selectedCat.name}
            </h5>
            <div
              className="emissionsTag"
              style={{
                backgroundColor:
                  climateScoreColors[
                    Math.round(
                      selectedCat === "all" ? totalAvg : selectedCat.climateAvg
                    )
                  ],
              }}
            >
              <p id="categoryEmissionTagText">
                {selectedCat === "all" ? totalAvg : selectedCat.climateAvg}
              </p>
            </div>
          </div>

          <div id="emissionsCategoryLegend">
            {data.map((item, index) => {
              return (
                <React.Fragment>
                  <div
                    className="emissionCategoryRow"
                    onClick={() =>
                      selectedCat === "all"
                        ? setSelectedCat(item)
                        : selectedSubCat === item
                        ? setSelectedSubCat("")
                        : setSelectedSubCat(item)
                    }
                    key={`emissionRow-${index}`}
                    style={{
                      backgroundColor:
                        index % 2 === 0 ? "rgba(0,0,0,0.1)" : "white",
                    }}
                  >
                    <span className="emissionRowTitleWrapper">
                      <span className="emissionRowTitleSpan">
                        <div
                          className="tinyCircle"
                          style={{ backgroundColor: item.color }}
                        />
                        <p className="emissionCategoryRowText">{item.name}</p>
                      </span>
                      <div
                        className="emissionsTag"
                        style={{
                          backgroundColor:
                            climateScoreColors[Math.round(item.climateAvg)],
                        }}
                      >
                        <p className="emissionCategoryRowText">
                          {item.climateAvg}
                        </p>
                      </div>
                    </span>
                    {selectedSubCat !== "" &&
                      selectedSubCat.name === item.name && (
                        <div className="itemsBySubCatList">
                          {itemsBySubCat[selectedSubCat.name].map(
                            (item, index) => {
                              return (
                                <div
                                  className="emissionItemRow"
                                  key={`emissionRow-${index}`}
                                >
                                  <p className="emissionItemText">
                                    {item.name}
                                  </p>
                                  <div
                                    className="emissionScoreCircle"
                                    style={{
                                      backgroundColor:
                                        climateScoreColors[item.climateScore],
                                    }}
                                  >
                                    <p className="emissionItemScore">
                                      {item.climateScore}
                                    </p>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </React.Fragment>
      ) : (
        <p>Emissions per category</p>
      )}
    </div>
  );
};

export default EmissionsPerCategory;
